import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRoutes } from 'hooks/useRoutes';
import { getAssetUrl } from 'utils/getAssetUrl';
import PerfectScrollbar from 'perfect-scrollbar';
import trnsactDefaultLogo from 'assets/img/trnsact-logo-dark.png';
import { List, Collapse, Divider, Box } from '@mui/material';
import { UserProfile, VendorProfile } from '@trnsact/trnsact-shared-types';
import { SidebarLogo } from './SidebarLogo';
import { LogoutNavItem } from './LogoutNavItem';
import { StyledDrawer } from './StyledDrawer';
import { SidebarNavItem } from './SidebarNavItem';
import { SidebarNeedHelpSection } from './SidebarNeedHelpSection';
import { SidebarUserSection } from './SidebarUserSection';
import { SidebarStyledContainer } from './SidebarStyledContainer';
import { vendorProfileDemoLogoPath } from '../../pages/Prequal/constants';
import { ToggleSidebarSection } from './ToggleSidebarSection';

interface Props {
  isDrawerOpen: boolean;
  toggleSidebar: () => void;
  onNotificationsIconClick: () => void;
}

export const SidebarV2 = ({ isDrawerOpen, toggleSidebar, onNotificationsIconClick }: Props) => {
  const [isMenuCollapsed, setIsMenuCollapsed] = useState<boolean>(true);

  const sidebarWrapperRef = useRef<any>();
  const logoRef = useRef<any>();

  useEffect(() => {
    sidebarWrapperRef.current &&
      new PerfectScrollbar(sidebarWrapperRef.current.firstChild, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
  }, [sidebarWrapperRef]);

  const vendorProfile = useSelector<any, VendorProfile>(state => state.vp);
  const userProfile = useSelector<any, UserProfile>(state => state.userProfile);

  const { availableAppRoutes, availalbeUserRoutes } = useRoutes();

  const userRoutes = availalbeUserRoutes?.filter(({ showInSidebarNav }) => showInSidebarNav);
  const adminRoutes = availableAppRoutes?.filter(({ showInSidebarNav }) => showInSidebarNav);

  const vendorLogoPath = userProfile?.isDemoMode ? vendorProfileDemoLogoPath : vendorProfile?.logo;

  const logoUrl = vendorLogoPath ? getAssetUrl(vendorLogoPath) : trnsactDefaultLogo;
  const userProfileMugshot = getAssetUrl(userProfile.mugshot ?? '');

  return (
    <StyledDrawer ref={sidebarWrapperRef} anchor="left" variant="permanent" open={isDrawerOpen}>
      <SidebarStyledContainer
        logoHeight={logoRef?.current?.clientHeight}
        backgroundColor={vendorProfile?.logoColors?.logoComplementaryColor || '#FFFFFF'}
        isDrawerOpen={isDrawerOpen}
      >
        <Box ref={logoRef}>
          <SidebarLogo logoUrl={logoUrl} />
        </Box>

        <List className="navList">
          <Divider orientation="horizontal" className="divider" />

          <ToggleSidebarSection isDrawerOpen={isDrawerOpen} toggleSidebar={toggleSidebar} />

          <Divider orientation="horizontal" className="divider" />

          <SidebarUserSection
            isDrawerOpen={isDrawerOpen}
            isCollapsed={isMenuCollapsed}
            setIsCollapsed={setIsMenuCollapsed}
            userName={userProfile.fullName ?? ''}
            userProfileMugshot={userProfileMugshot}
            onNotificationsIconClick={onNotificationsIconClick}
          />

          <Collapse in={!isMenuCollapsed}>
            <List>
              {userRoutes.map(route => (
                <SidebarNavItem key={route.id} routeConfig={route} />
              ))}
              <LogoutNavItem />
            </List>
          </Collapse>

          <Divider orientation="horizontal" className="divider" />

          {adminRoutes.map(route => (
            <SidebarNavItem key={route.id} routeConfig={route} />
          ))}
        </List>

        <SidebarNeedHelpSection isDrawerOpen={isDrawerOpen} />
      </SidebarStyledContainer>
    </StyledDrawer>
  );
};
